import { Component, NgZone } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { Network } from '@capacitor/network';
import { SplashScreen } from '@capacitor/splash-screen';
import { TranslateService } from '@ngx-translate/core';
import { AuthenticationService } from './_services/authentication.service';
import { StatusBar, Style } from '@capacitor/status-bar';
import { environment } from 'src/environments/environment';
import { GENDER_HE, GENDER_SHE, GENDER_THEY, User } from './_models';

import { Geolocation, Position } from '@capacitor/geolocation';
import { App, URLOpenListenerEvent } from '@capacitor/app';
import { NavigationEnd, Router } from '@angular/router';
import { AlertController, Platform } from '@ionic/angular';
import { SettingsService } from './_services/settings.service';
import { Badge } from '@capawesome/capacitor-badge';
import { EventsService } from './_services/events.service';
import { StatsSummary } from './_models/statsSummary';
import { StateService } from './_services/state.service';
import { HelperService } from './_helpers/helper.service';
import { DebugService } from './_services/debug.service';
import { AdsTrackingService } from './_services/ads-tracking.service';
import { PushNotifications } from '@capacitor/push-notifications';
import { LocalDataService } from './_services/localData.service';
import { PurchaseService } from './_services/purchase.service';
import { SubscriptionService } from './_services/subscription.service';
import { filter } from 'rxjs/operators';
import { ApiService } from './_services/api.service';

@Component({
    selector: 'app-root',
    templateUrl: 'app.component.html',
    styleUrls: ['app.component.scss']
})
export class AppComponent {
    environment = environment;
    showNonProductionWarning = !environment.production;
    nonProductionEnvironmentLabel = this.getNonProductionEnvironmentLabel();

    private alert: HTMLIonAlertElement;
    private connected: boolean = true;
    private lastTrackedScreenPath: string = null;
    private signInTrackedThisLaunch: boolean = false;

    /**
     * Constructor
     */
    constructor(
        private eventsService: EventsService,
        private stateService: StateService,
        private settingsService: SettingsService,
        private purchaseService: PurchaseService,
        private authenticationService: AuthenticationService,
        private translateService: TranslateService, 
        private subscriptionService: SubscriptionService, 
        private zone: NgZone,
        private router: Router,
        private platform:Platform,
        private alertCtrl: AlertController,
        private helperService: HelperService,
        private debugService: DebugService,
        private localDataService: LocalDataService,
        private adsTrackingService: AdsTrackingService,
        private apiService: ApiService,
    ) {
        this.init();
    }

    /**
     * Init App
     */
    async init() {
        this.translateService.setDefaultLang('fr');
        this.translateService.use('fr');
        this.adsTrackingService.setConsentScope(null);
        this.authenticationService.init();
        this.localDataService.creatDataDirPath();

        this.authenticationService.user.subscribe((user: User) => {
            environment.theme = null;
            if (user) {
                this.authenticationService.loadMe().subscribe(async responseData => {
                    this.subscriptionService.manageCurrentSubscription();
                    this.debugService.init(this.authenticationService.isAdmin, this.authenticationService.meValue?.debug);
                    this.debugService.send("App init");

                    setTimeout(() => {
                        this.purchaseService.startup();
                    }, 5000);

                    if (user.gender == GENDER_THEY) {
                        if (user.wanted_gender.includes(GENDER_SHE)) {
                            environment.theme = GENDER_SHE;
                        } else {
                            environment.theme = GENDER_HE;
                        }
                    } else {
                        environment.theme = user.gender;
                    }
                    
                    this.hideSplash();
                    
                    if (this.authenticationService.meValue) {
                        this.adsTrackingService.setConsentScope(this.authenticationService.meValue);
                        await this.adsTrackingService.syncUserProperties(this.authenticationService.meValue);
                        this.lastTrackedScreenPath = (this.router.url || '').split('?')[0];
                        
                        await this.maybeRequestTracking();
                        await this.trackLaunchSignIn();

                        const coordinates:Position = await Geolocation.getCurrentPosition();
                        this.apiService.setUserPosition(this.authenticationService.meValue['@id'], coordinates.coords.latitude, coordinates.coords.longitude).subscribe();

                        let phoneValid = this.authenticationService.meValue?.phoneNumber != null && this.helperService.checkPhone(this.authenticationService.meValue?.phoneCode, this.authenticationService.meValue?.phoneNumber);
                        
                        if (!phoneValid) {
                            const alert = await this.alertCtrl.create({
                                header: this.translateService.instant('ERRORS.PHONE.TITLE'),
                                message: this.translateService.instant('ERRORS.PHONE.TEXT'),
                                buttons: [{
                                    text: this.translateService.instant('COMMON.NO'),
                                    role: 'cancel',
                                    handler: () => {
                                    },
                                }, {
                                    text: this.translateService.instant('COMMON.YES'),
                                    role: 'confirm',
                                    handler: () => {
                                        this.router.navigate(['/', 'donnees-personnelles', 'telephone']);
                                    },
                                },
                                ],
                            });
                        
                            // await alert.present();
                        }
                    }
                });
            } else {
                this.signInTrackedThisLaunch = false;
                this.adsTrackingService.setConsentScope(null);
                this.hideSplash();
            }
        });

        Badge.isSupported().then(async result => {
            
            if (result.isSupported && Capacitor.isNativePlatform()) {

                if (Capacitor.isPluginAvailable('PushNotifications')) {
                    let permStatus = await PushNotifications.checkPermissions();
                    if (permStatus.receive === 'prompt' || permStatus.receive === 'prompt-with-rationale') {
                        permStatus = await PushNotifications.requestPermissions();
                    }
                    if (permStatus.receive !== 'granted') {
                        throw new Error('User denied permissions!');
                    }
                    await PushNotifications.register();
                }

                let perms = await Badge.checkPermissions();

                if (perms.display == 'denied') {
                    let result = await Badge.requestPermissions();
                }
                
                if (perms.display != 'denied') {
                    await Badge.clear();
                        
                    let count = this.stateService.stats?.acceptedCrushes + this.stateService.stats?.albumRequests + this.stateService.stats?.conversations + this.stateService.stats?.receivedCrushes + this.stateService.stats?.visits + this.stateService.stats?.publicRooms + this.stateService.stats?.privateRooms + this.stateService.stats?.favoriteRooms + this.stateService.stats?.widgetMessages;
                    await Badge.set({ count });
                }

                this.eventsService.updateStatsEvent.subscribe(async (data: StatsSummary) => {
                    if (!data) {
                        await Badge.clear();  
                    } else {
                        let count = data.acceptedCrushes + data.albumRequests + data.conversations + data.receivedCrushes + data.visits + data.privateRooms + data.publicRooms + data.favoriteRooms + data.widgetMessages;
                        await Badge.set({ count });
                    }
                });
            }
        });

        App.addListener('appUrlOpen', (event: URLOpenListenerEvent) => {
            this.zone.run(() => {
                const domain = 'begaym.com';
                const pathArray = event.url.split(domain);
                const appPath = pathArray.pop().replace('begaymapp:/', '');

                if (appPath) {
                    this.router.navigateByUrl(appPath);
                }
            });
        });

        this.router.events.pipe(
            filter((event) => event instanceof NavigationEnd)
        ).subscribe((event: NavigationEnd) => {
            const url = event.urlAfterRedirects || event.url;
            const path = (url || '').split('?')[0];
            if (path === this.lastTrackedScreenPath) {
                return;
            }

            this.lastTrackedScreenPath = path;
            this.adsTrackingService.trackScreenView(url, this.authenticationService.meValue);
            this.adsTrackingService.trackCampaignFromUrl(url, this.authenticationService.meValue);
        });

        this.initCookieConsent();

        this.platform.ready().then(() => {
            this.settingsService.startup();
        });

        Network.addListener('networkStatusChange', async (status) => {
            this.connected = status.connected;
            this.manageConnectivity();
        });
    }

    closeNonProductionWarning(): void {
        this.showNonProductionWarning = false;
    }

    private async maybeRequestTracking() {

        if (!Capacitor.isNativePlatform()) {
            return;
        }

        const isIos = Capacitor.getPlatform() === 'ios';

        if (isIos) {
            const attAllowed = await this.adsTrackingService.ensureAttAuthorizationForTracking();
            this.adsTrackingService.setTrackingConsent(attAllowed);
            await this.adsTrackingService.syncUserProperties(this.authenticationService.meValue);
            if (!attAllowed) {
                return;
            }
        } else {
            if (!this.adsTrackingService.hasTrackingConsentChoice()) {
                const alert = await this.alertCtrl.create({
                    header: 'Autoriser le suivi',
                    message: 'Pour mesurer et optimiser les performances de Begaym, nous utilisons le suivi publicitaire. Tu peux modifier ton choix à tout moment.',
                    backdropDismiss: false,
                    buttons: [{
                        text: this.translateService.instant('COMMON.NO'),
                        role: 'cancel',
                        handler: async () => {
                            this.adsTrackingService.setTrackingConsent(false);
                            await this.adsTrackingService.syncUserProperties(this.authenticationService.meValue);
                        }
                    }, {
                        text: this.translateService.instant('COMMON.YES'),
                        role: 'confirm',
                        handler: async () => {
                            this.adsTrackingService.setTrackingConsent(true);
                            await this.adsTrackingService.syncUserProperties(this.authenticationService.meValue);
                            await this.maybeRequestTracking();
                            await this.trackLaunchSignIn();
                        }
                    }]
                });

                await alert.present();
                return;
            }

            if (!this.adsTrackingService.hasTrackingConsent()) {
                return;
            }
        }

        if (Capacitor.isNativePlatform()) {
            const installKey = 'tracking_install_sent';
            if (!localStorage.getItem(installKey)) {
                this.adsTrackingService.trackInstall();
                localStorage.setItem(installKey, new Date().toISOString());
            }
        }
    }

    private getNonProductionEnvironmentLabel(): string {
        const websiteUrl = environment.websiteUrl || '';

        if (websiteUrl.includes('preprod')) {
            return 'PREPROD';
        }

        if (websiteUrl.includes('localhost')) {
            return 'DEV';
        }

        return 'NON PROD';
    }

    private async trackLaunchSignIn() {
        if (this.signInTrackedThisLaunch) {
            return;
        }

        if (!this.authenticationService.meValue || !this.adsTrackingService.hasTrackingConsent()) {
            return;
        }

        await this.adsTrackingService.trackSignIn('app_launch');
        this.signInTrackedThisLaunch = true;
    }

    /**
     * Manage connection status
     */
    private manageConnectivity() {
        if (this.alert) {
            this.alert.dismiss();
            this.alert = null;
        }
        
        if (!this.connected) {
            // this.alertCtrl.create({
            //     header: 'Connexion',
            //     message: 'Une connexion internet est nécessaire pour utiliser Begaym',
            //     backdropDismiss: false
            // }).then((element) => {
            //     if (!this.connected) {
            //         this.alert = element;
            //         element.present();

            //         setTimeout(() => {
            //             if (this.alert) {
            //                 this.alert.dismiss();
            //                 this.alert = null;
            //             }
            //         }, 5000)
            //     }
            // });
        } else {
            this.debugService.send("/!\\ Network recovered /!\\");
        }
    }

    /**
     * Init cookie consent
     */
    private initCookieConsent() {
        if (!Capacitor.isNativePlatform()) {

            (<any>window).axeptioSettings = {
                clientId: environment.axeptioID,
            };

            if (-1 !== navigator.userAgent.indexOf('; Need-Consent=0')) {
                return;
            }

            var t = document.getElementsByTagName('script')[0], e = document.createElement('script');
            e.async = true; e.src = "//static.axept.io/sdk.js";
            t.parentNode.insertBefore(e, t);

            void 0 === (<any>window)._axcb && ((<any>window)._axcb = []);
            (<any>window)._axcb.push((axeptio) => {
                axeptio.on("cookies:complete", (choices) => {
                    if (choices.facebook_pixel) {
                        this.launchFB();
                    }
                    if (choices.google_analytics) {
                        this.launchGoogleAnalytics();
                    }
                    (<any>window).hideAxeptioButton();
                });

                axeptio.on("close", () => {
                    (<any>window).hideAxeptioButton();
                });
            });
        }
    }

    private launchFB() {
        
        if ((<any>window)) return;
        let n:any = (<any>window) = function () {
            n.callMethod ?
                n.callMethod.apply(n, arguments) : n.queue.push(arguments)
        };
        if (!(<any>window)._fbq) (<any>window)._fbq = n;
        n.push = n;
        n.loaded = !0;
        n.version = '2.0';
        n.queue = [];
        let t:any = document.createElement('script');
        t.async = !0;
        t.src = 'https://connect.facebook.net/en_US/fbevents.js';
        let s:any = document.getElementsByTagName('script')[0];
        s.parentNode.insertBefore(t, s);
    
        (<any>window).fbq('init', environment.fbID);
        (<any>window).fbq('track', 'PageView');
    }

    private launchGoogleAnalytics() {
        (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
        new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
        j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';(<any> j).async=true;(<any> j).src=
        'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
        })(window,document,'script','dataLayer', environment.gaID);
    }

    private hideWidget() {
        const widget:any = document.querySelector('.axeptio_mount');
        widget.style.display="none";
    }
    
    private openWidget() {
        const widget:any = document.querySelector('.axeptio_mount');
        widget.style.display="block";
        (<any>window).openAxeptioCookies();
    }

    /**
     * Hide splash
     */
    private hideSplash() {
        if (Capacitor.isPluginAvailable('StatusBar')) {
            setTimeout(_ => {
                // setOverlaysWebView() et setBackgroundColor() ne font plus rien depuis
                // l'API 35 : l'edge-to-edge est impose et les barres sont transparentes.
                // La couleur des icones reste pilotable ; l'etat initial est pose par
                // SystemBars.style dans capacitor.config.json, sans attendre 1 s.
                StatusBar.setStyle({
                    style: Style.Light
                })
            }, 1000);
        }

        if (Capacitor.isPluginAvailable('SplashScreen')) {
            SplashScreen.hide();
        }
    }  
}
